import {AnchoredOverlay, Button, IconButton, Tooltip, type ButtonBaseProps} from '@primer/react'
import type React from 'react'
import {useState} from 'react'
import {CodeIcon, TriangleDownIcon} from '@primer/octicons-react'
import {usePrefetchCodespacesData} from '@github-ui/codespaces-tab/components/use-codespaces-data'
import {preloadCodespacesTabReact} from '@github-ui/codespaces-tab/components/CodespacesTab'

// The props AnchoredOverlay passes to renderAnchor: standard HTML attributes for the anchor
// element. AnchoredOverlay also attaches a `ref` at runtime that isn't part of this type.
type AnchorProps = Omit<React.HTMLAttributes<HTMLElement>, 'aria-label' | 'aria-labelledby'>

export interface CodeMenuButtonProps {
  children: React.ReactNode
  className?: string
  size?: 'small' | 'large' | 'medium'
  onOpenChange?: (open: boolean) => void
  onMenuOpen?: () => void
  codespacesPath?: string
  shouldPrefetchCodespaces?: boolean
  iconOnly?: boolean
  variant?: ButtonBaseProps['variant']
  trailingVisual?: boolean
}

export const CodeMenuButton = ({
  children,
  className,
  size,
  onOpenChange,
  onMenuOpen,
  codespacesPath,
  variant,
  shouldPrefetchCodespaces = false,
  iconOnly = false,
  trailingVisual = false,
}: CodeMenuButtonProps) => {
  const [showPopover, setShowPopover] = useState(false)
  const prefetchJson = usePrefetchCodespacesData(codespacesPath)
  const resolvedSize = size ?? 'medium'

  const prefetch = () => {
    if (!shouldPrefetchCodespaces) return

    preloadCodespacesTabReact()
    prefetchJson()
  }

  // AnchoredOverlay forwards a ref plus interaction handlers to whichever element we render
  // as the anchor. These aren't reflected in renderAnchor's prop type, so we narrow them here.
  const renderAnchor = (anchorProps: AnchorProps) => {
    const {ref, ...interactionProps} = anchorProps as AnchorProps & {ref?: React.Ref<HTMLElement>}

    // Full button: leading code icon, visible "Code" label, trailing chevron.
    if (!iconOnly) {
      return (
        <Button
          {...interactionProps}
          ref={ref as React.Ref<HTMLButtonElement>}
          className={className}
          variant={variant}
          leadingVisual={() => <CodeIcon className="hide-sm" />}
          trailingVisual={TriangleDownIcon}
          size={resolvedSize}
          onMouseEnter={prefetch}
          onFocus={prefetch}
        >
          Code
        </Button>
      )
    }

    // Icon-only, no chevron: IconButton gives proper square styling and its own built-in
    // tooltip, so no wrapping Tooltip is needed.
    if (!trailingVisual) {
      return (
        <IconButton
          {...interactionProps}
          ref={ref as React.Ref<HTMLButtonElement>}
          className={className}
          variant={variant}
          icon={CodeIcon}
          aria-label="Code"
          size={resolvedSize}
          onMouseEnter={prefetch}
          onFocus={prefetch}
        />
      )
    }

    // Icon-only with chevron: IconButton can't render a trailingVisual so we use a Button. Button has no
    // built-in tooltip, so wrap it in one.
    return (
      <Tooltip ref={ref} text="Code" direction="sw" _privateDisableTooltip={showPopover}>
        <Button
          {...interactionProps}
          ref={ref as React.Ref<HTMLButtonElement>}
          aria-label="Code"
          className={className}
          variant={variant}
          trailingVisual={TriangleDownIcon}
          size={resolvedSize}
          onMouseEnter={prefetch}
          onFocus={prefetch}
        >
          <CodeIcon />
        </Button>
      </Tooltip>
    )
  }

  return (
    <AnchoredOverlay
      align="end"
      focusZoneSettings={{disabled: true}}
      open={showPopover}
      onOpen={() => {
        prefetch()
        setShowPopover(true)
        onMenuOpen?.()
        onOpenChange?.(true)
      }}
      onClose={() => {
        setShowPopover(false)
        onOpenChange?.(false)
      }}
      renderAnchor={renderAnchor}
    >
      {children}
    </AnchoredOverlay>
  )
}
